import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home, Hotel, HardHat, Sprout, Landmark, Building, Factory,
  Droplets, Gauge, CloudRain, Waves, Activity, Recycle, Wrench, ShieldCheck,
  MapPin, Calendar, ClipboardCheck, Hammer,
} from "lucide-react";
import { INDUSTRIES, INDUSTRY_OFFERINGS, STANDARDS_BADGES } from "../lib/content";
import FAQ from "../components/FAQ";
import SEO, { faqLd, serviceLd, breadcrumbLd } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";

const fadeUp = { hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.2, 0.7, 0.2, 1] } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.05 } } };

// Industry → icon + accent (brand palette only)
const INDUSTRY_META = {
  lodges:           { icon: Hotel,     accent: "#1FB6C1" },
  estates:          { icon: Home,      accent: "#0C2A40" },
  "food-beverage":  { icon: Factory,   accent: "#1D6A8C" },
  mines:            { icon: HardHat,   accent: "#0C2A40" },
  agriculture:      { icon: Sprout,    accent: "#42954B" },
  municipal:        { icon: Landmark,  accent: "#1FB6C1" },
  residential:      { icon: Building,  accent: "#1D6A8C" },
};

// Offering slug → icon + accent (matches Home / Solutions vocabulary)
const OFFERING_META = {
  purification:     { icon: Droplets, accent: "#1FB6C1" },
  wastewater:       { icon: Recycle,  accent: "#42954B" },
  pumps:            { icon: Gauge,    accent: "#0C2A40" },
  harvesting:       { icon: CloudRain, accent: "#1D6A8C" },
  "pool-spa":       { icon: Waves,    accent: "#1FB6C1" },
  irrigation:       { icon: Sprout,   accent: "#42954B" },
  monitoring:       { icon: Activity, accent: "#0C2A40" },
  "solar-cleaning": { icon: Activity, accent: "#1D6A8C" },
  service:          { icon: Wrench,   accent: "#1D6A8C" },
};

// Per-industry, per-offering tile photo. Path convention:
//   /generated/industries/<industry>/<offering>.png
// Falls back to OFFERING_PHOTO (the legacy single-photo map) if the
// industry-specific render isn't on disk yet.
const industryPhoto = (industrySlug, offeringSlug) =>
  `/generated/industries/${industrySlug}/${offeringSlug}.png`;

// Legacy single-photo fallback (used if the industry-specific image is missing)
const U = (id) => `https://images.unsplash.com/${id}?w=1200&q=80&auto=format&fit=crop`;
const OFFERING_PHOTO = {
  purification:     U("photo-1636649389054-e5dcea139e64"),
  wastewater:       U("photo-1622322977875-e2d580e129ba"),
  pumps:            U("photo-1705708551758-76b153fa536e"),
  harvesting:       U("photo-1622322977781-76f2247964b7"),
  "pool-spa":       U("photo-1533077162801-86490c593afb"),
  irrigation:       U("photo-1705708551626-78f6a10eab5e"),
  monitoring:       "/mockups/eq_monitoring_sensor.webp",
  "solar-cleaning": "/mockups/eq_solar_robot.webp",
  service:          "/projects/thornybush/thornybush_07.webp",
};

// Track which industry-specific renders exist so we can fall back cleanly.
// (Vite/CRA serves /public from disk, so the <img onError> handler does the
// actual fallback at render-time.)

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = INDUSTRIES.find((i) => i.slug === slug);
  if (!ind) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center text-center px-6 pt-32">
        <div>
          <div className="eyebrow mb-4 text-black/50">404</div>
          <h1 className="font-display text-3xl md:text-5xl mb-6">Industry not found.</h1>
          <Link to="/industries" className="hv-magnetic">Back to industries <span aria-hidden>→</span></Link>
        </div>
      </div>
    );
  }

  const meta = INDUSTRY_META[slug] || { icon: Building, accent: "#1FB6C1" };
  const accent = meta.accent;
  const IndIcon = meta.icon;

  const relatedIndustries = INDUSTRIES.filter((i) => i.slug !== slug).slice(0, 3);

  const buyerQuestions = (ind.whatToLookFor || []).map(([q, a]) => [q, a]);
  const allFaqs = [...buyerQuestions, ...(ind.faqs || [])];
  const offerings = INDUSTRY_OFFERINGS[slug] || [];

  const breadcrumbItems = [
    ["Home", "/"],
    ["Industries", "/industries"],
    [ind.name, `/industries/${slug}`],
  ];
  const combinedLd = {
    "@context": "https://schema.org",
    "@graph": [serviceLd(ind.name, ind.tagline), faqLd(ind.faqs || []), breadcrumbLd(breadcrumbItems)],
  };

  return (
    <div data-testid={`industry-page-${slug}`}>
      <SEO title={ind.name} description={ind.metaDesc} path={`/industries/${slug}`} image={ind.hero} ldJson={combinedLd} />

      {/* HERO */}
      <section className="eng-hero">
        <div className="eh-bg" aria-hidden><img src={ind.hero} alt={ind.name} loading="lazy" decoding="async"/></div>
        <div className="eh-veil" aria-hidden />
        <div className="eh-content">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp} className="mb-4">
              <Breadcrumb items={breadcrumbItems} theme="overlay" testid="industry-breadcrumb" />
            </motion.div>
            <motion.h1 variants={fadeUp}>{ind.name}</motion.h1>
            <motion.p variants={fadeUp} className="eh-lede">{ind.tagline}</motion.p>
            <motion.div variants={fadeUp} className="eh-actions">
              <Link to="/contact" className="hv-magnetic" data-testid="industry-cta-survey">
                Discuss your water needs <span aria-hidden>→</span>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* OVERVIEW */}
      {/* OVERVIEW — pb tightened because the next section is also bg-white;
          previously the stacked py-14/py-20 created a visible empty gap. */}
      <section className="bg-white px-6 md:px-12 lg:px-20 pt-14 md:pt-20 pb-8 md:pb-10" data-testid="industry-overview">
        <div className="max-w-[1100px] mx-auto">
          <h2 className="font-display mb-10" style={{ fontSize: "clamp(1.8rem, 3.8vw, 3rem)", lineHeight: 1.12, letterSpacing: "-0.015em" }}>
            {ind.short}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 md:gap-10">
            {ind.openingParas.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.08 }}>
                <span className="block w-8 h-px mb-4" style={{ background: accent }} aria-hidden />
                <p className="text-[0.98rem] font-light text-black/75 leading-relaxed">{p}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GROUP STANDARD — only renders on industries that opt in (e.g. lodges,
          where the buyer is a multi-site brand-standards manager, not a single GM). */}
      {ind.groupStandard && (
        <section
          className="px-6 md:px-12 lg:px-20 py-12 md:py-16"
          style={{ background: "#FAFAF7", color: "#0C2A40" }}
          data-testid="industry-group-standard"
        >
          <div className="max-w-[1100px] mx-auto">
            <div
              className="font-mono uppercase mb-4"
              style={{ fontSize: "0.62rem", letterSpacing: "0.28em", color: accent }}
            >
              {ind.groupStandard.eyebrow}
            </div>
            <h2
              className="font-display mb-6"
              style={{
                fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)",
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
                fontWeight: 500,
                color: "#0C2A40",
              }}
            >
              {ind.groupStandard.title}
            </h2>
            <p className="text-[1rem] font-light leading-relaxed" style={{ color: "rgba(26,31,37,0.82)", maxWidth: "72ch" }}>
              {ind.groupStandard.body}
            </p>
          </div>
        </section>
      )}

      {/* OUR SERVICES — offerings funnel back into /services/:slug.
          pt tightened because Overview above is also bg-white. */}
      {offerings.length > 0 && (
        <section className="bg-white px-6 md:px-12 lg:px-20 pt-6 md:pt-10 pb-14 md:pb-20" data-testid="industry-offerings">
          <div className="max-w-[1500px] mx-auto">
            <h2 className="font-display mb-8 md:mb-10" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40" }}>
              What we deliver for {ind.name.toLowerCase()}.
            </h2>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="spec-grid">
              {offerings.map((o) => {
                const om = OFFERING_META[o.slug] || { icon: Droplets, accent };
                const OIcon = om.icon;
                const photo = industryPhoto(slug, o.slug);
                const fallback = OFFERING_PHOTO[o.slug];
                return (
                  <motion.div key={o.slug} variants={fadeUp}>
                    <Link to={`/services/${o.slug}`} className="outcome-tile light" style={{ "--ot-accent": om.accent }} data-testid={`industry-offering-${o.slug}`}>
                      <div className="ot-icon"><OIcon /></div>
                      <div className="ot-title" dangerouslySetInnerHTML={{ __html: o.title }} />
                      <p className="ot-lede">{o.lede}</p>
                      <ul className="mt-3 space-y-2">
                        {o.bullets.slice(0, 3).map((b, bi) => (
                          <li key={bi} className="flex items-start gap-2.5 text-[0.88rem] font-light text-black/75 leading-relaxed">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: om.accent }} />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="ot-cta">
                        <span>Read more</span>
                        <span className="ot-cta-arrow" aria-hidden>→</span>
                      </div>
                      {photo && (
                        <div className="ot-photo" aria-hidden>
                          <img
                            src={photo}
                            alt=""
                            loading="lazy"
                            onError={(e) => {
                              // Fall back to the generic per-offering photo;
                              // only hide the strip if the fallback is missing too.
                              const img = e.currentTarget;
                              if (fallback && img.src !== fallback && !img.src.endsWith(fallback)) {
                                img.src = fallback;
                              } else {
                                img.parentElement.style.display = "none";
                              }
                            }}
                          />
                        </div>
                      )}
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>
      )}

      {/* FAQ — Buyer's guide, light hybrid */}
      <FAQ faqs={allFaqs} />

      {/* WHY HIDROVERSE — light hybrid theme */}
      {ind.whyHidroverse && (
        <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20" style={{ background: "#FFFFFF", color: "#0C2A40" }} data-testid="industry-why-hv">
          <div className="max-w-[1500px] mx-auto">
            <h2 className="font-display mb-8 md:mb-10" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40" }}>
              What you get when signing with us.
            </h2>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="spec-grid cols-2">
              {ind.whyHidroverse.map(([title, body]) => (
                <motion.div key={title} variants={fadeUp}>
                  <div className="outcome-tile light" style={{ "--ot-accent": accent }}>
                    <div className="ot-icon"><ShieldCheck /></div>
                    <div className="ot-title">{title}</div>
                    <p className="ot-lede">{body}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* TRUSTED-BY CLIENT CHIPS — surfaces the full clients[] list from
          industries.js as a horizontal chip strip. Renders only when the
          industry has a non-empty clients array. Names longer than a chip
          get a subtle underline; confidential entries stay as-is. */}
      {ind.clients && ind.clients.length > 0 && (
        <section className="px-6 md:px-12 lg:px-20 py-10 md:py-14"
                 style={{ background: "#FAFAF7", color: "#0C2A40" }}
                 data-testid="industry-trusted-by">
          <div className="max-w-[1500px] mx-auto">
            <div className="font-mono uppercase mb-4"
                 style={{ fontSize: "0.7rem", letterSpacing: "0.24em", color: "rgba(12,42,64,0.55)", fontWeight: 600 }}>
              Trusted by
            </div>
            <ul className="flex flex-wrap gap-2 md:gap-3" data-testid="industry-clients-list">
              {ind.clients.map((client) => (
                <li key={client}
                    data-testid={`industry-client-chip-${client.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    className="px-4 py-2 font-light"
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid rgba(12,42,64,0.14)",
                      color: "#0C2A40",
                      fontSize: "0.92rem",
                      borderLeft: `2px solid ${accent}`,
                    }}>
                  {client}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* KEY METRIC — only renders when the industry provides one. */}
      {ind.metric && ind.metric[0] && (
        <section className="bg-white px-6 md:px-12 lg:px-20 py-14 md:py-20 text-center" data-testid="industry-metric">
          <div className="max-w-[1100px] mx-auto">
            <div className="font-display" style={{ fontSize: "clamp(3.4rem, 12vw, 9rem)", color: accent, lineHeight: 0.9, letterSpacing: "-0.045em", fontWeight: 200 }}>
              {ind.metric[0]}
            </div>
            <p className="mt-7 max-w-xl mx-auto font-light text-[1rem] text-black/70 leading-relaxed">{ind.metric[1]}</p>
          </div>
        </section>
      )}

      {/* PROJECT TIMELINE — only renders when the industry provides a timeline. */}
      {ind.timeline && (
      <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20" style={{ background: "#FAFAF7", color: "#0C2A40" }} data-testid="industry-timeline-section">
        <div className="max-w-[1500px] mx-auto">
          <h2 className="font-display mb-8 md:mb-10" style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.2rem)", lineHeight: 1.15, letterSpacing: "-0.015em", fontWeight: 500, color: "#0C2A40" }}>
            Survey to commissioning.
          </h2>

          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="spec-grid cols-4 mb-6">
            {[
              { icon: MapPin,         label: "Survey",        value: ind.timeline.survey },
              { icon: ClipboardCheck, label: "Design",        value: ind.timeline.design },
              { icon: Hammer,         label: "Install",       value: ind.timeline.install },
              { icon: Calendar,       label: "Commissioning", value: ind.timeline.commissioning },
            ].map((p) => {
              const PIcon = p.icon;
              return (
                <motion.div key={p.label} variants={fadeUp}>
                  <div className="outcome-tile light" style={{ "--ot-accent": accent }}>
                    <div className="ot-icon"><PIcon /></div>
                    <div className="ot-title">{p.label}</div>
                    <p className="ot-lede" style={{ marginBottom: 0 }}>{p.value}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
          <p className="text-center text-[0.95rem] font-light" style={{ color: "rgba(26,31,37,0.75)" }} data-testid="industry-pricing-note">
            <strong style={{ color: accent, fontWeight: 600 }}>{ind.timeline.total}</strong> Every site
            has its own feed water, demand profile and civils — we quote fixed-price after the survey.
          </p>
        </div>
      </section>
      )}

      {/* WHAT'S INCLUDED / NOT INCLUDED — removed per user feedback */}

      {/* STANDARDS */}
      <section className="bg-[#fbfbfa] py-10 md:py-12 px-6 md:px-12 lg:px-20 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-[1500px] mx-auto flex flex-wrap items-center justify-between gap-6">
          <div className="eyebrow text-black/55" style={{ fontSize: "0.6rem", letterSpacing: "0.28em" }}>
            Designed to · Documented to · Defensible by
          </div>
          <div className="badge-strip">
            {STANDARDS_BADGES.map((b) => <span key={b} className="badge light">{b}</span>)}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="cta-strip">
        <div className="csi">
          <div>
            <h3>Brief us on your water needs.</h3>
            <p className="csi-meta">
              For personalised assistance and solutions, contact our specialists.
            </p>
          </div>
          <Link to="/contact" className="hv-magnetic" data-testid="industry-closing-cta" style={{ justifyContent: "center" }}>
            Contact us <span aria-hidden>→</span>
          </Link>
        </div>
      </section>

      {/* OTHER INDUSTRIES — photo tiles matching the /industries register */}
      <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20" style={{ background: "#FFFFFF", color: "#0C2A40" }}>
        <div className="max-w-[1500px] mx-auto">
          <div
            className="font-mono uppercase mb-8 md:mb-10"
            style={{ fontSize: "0.62rem", letterSpacing: "0.28em", color: "#1FB6C1" }}
          >
            Other Industries
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {relatedIndustries.map((r) => (
              <Link
                key={r.slug}
                to={`/industries/${r.slug}`}
                className="industry-tile block aspect-[16/11]"
                data-testid={`industry-related-${r.slug}`}
              >
                <img src={r.hero} alt={r.name} loading="lazy" />
                <div className="tile-overlay" />
                <div className="tile-label">
                  <div>
                    <div
                      className="font-mono uppercase mb-2"
                      style={{ fontSize: "0.58rem", letterSpacing: "0.24em", color: "rgba(255,255,255,0.82)" }}
                    >
                      Industry
                    </div>
                    <div
                      className="text-white"
                      style={{ fontSize: "1.15rem", fontWeight: 600, lineHeight: 1.2, letterSpacing: "-0.005em", maxWidth: "22ch" }}
                    >
                      {r.name}
                    </div>
                    <div
                      className="mt-2 font-light text-white/85"
                      style={{ fontSize: "0.86rem", lineHeight: 1.5, maxWidth: "38ch" }}
                    >
                      {r.short}
                    </div>
                  </div>
                  <div
                    className="self-end font-mono uppercase inline-flex items-center gap-2"
                    style={{ fontSize: "0.6rem", letterSpacing: "0.24em", color: "rgba(255,255,255,0.95)", fontWeight: 700 }}
                  >
                    Explore <span style={{ color: "#1FB6C1" }} aria-hidden>→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
